import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sortBy } from 'remeda';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { SlotKeys } from '@pizza-hut-us-development/client-core';
import { IngredientInternalName } from '../../../identifiers/constants';
import { SeeAllButton } from '../../ExpandButtons';
import { stripCharacters } from '@/common/string-formatter';
import Portion from '../../../../../common/Portion';
import { toIngredientWithPortionAndPlacement } from '../../../dataTransformers/toIngredient';
import { openModal } from '@/localization/actions';
import { selectors } from '../../../slice/pizza.slice';
import { onPizzaToppingClick, onPizzaToppingSeeAllClick } from '@/dataAnalytics/dataAnalyticsHelper';
import { ToppingsExtraAnalytics, ToppingsSelectionAnalytics } from '@/dataAnalytics/analyticsTypes';
import ToppingItemOptimized, { OnToppingItemChangeProps } from '../ToppingItem/ToppingItemOptimized';
import { selectors as modalSelectors, updatePanCrustToppingModalShown } from '../../../slice/modal/modal.slice';
import {
  CrustOption,
  PizzaIngredient,
  PizzaIngredientOption
} from '@/builders/pizza/dataTransformers/builderTypes';
import formattedPrice from '@/common/formattedPrice';
import useStyles from './ToppingsPicker.styles';
import { useLineup } from '@/builders/deals/hooks/useLineup';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import {
  DETROIT_PIZZA,
  CRISPY_CUPPED_PEPPERONI,
  BIG_NEW_YORKER_DOUBLE_PEPPERONI,
  HOT_HONEY_PARTIAL,
  TAVERN_PIZZA
} from '@/builders/pizza/constants';
import constants from '../../../constants';

const DEFAULT_MINIMUM_DISPLAYED_ITEMS = 3;

interface ToppingsPickerProps {
  title: 'Veggies' | 'Meats';
  toppingOptions: PizzaIngredientOption[];
  selectedToppings: PizzaIngredient[];
  onToppingsUpdate: (value: PizzaIngredient[]) => void;
  extraPrice?: number | null;
  reachedToppingLimit?: boolean;
  numDisplayedItems?: number;
  hideToppingsExtraPrice?: boolean;
  hideTitle?: boolean;
}

function shouldSeeAll(sortedToppingList: PizzaIngredientOption[], numDisplayedItems: number): boolean {
  const selectedToppingsNotAtTheTop = sortedToppingList.filter(
    ({ selected }, index) => selected && index >= numDisplayedItems
  );
  return Boolean(selectedToppingsNotAtTheTop.length);
}

function getToppingsList(toppings: PizzaIngredientOption[]): PizzaIngredientOption[] {
  return sortBy(
    toppings,
    (topping: PizzaIngredientOption) => topping.slotCode === SlotKeys.pizzaSeasoning,
    (topping: PizzaIngredientOption) => topping.priority || '',
    (topping: PizzaIngredientOption) => stripCharacters(topping.name)?.toLowerCase() || ''
  );
}

const isPanCrust = (crust: PizzaIngredient | null): boolean => !!crust?.isPanCrust;

const panCrustToppingMaxModalDetails = (
  ctaCallback: () => void,
  altCtaCallback: () => void,
  maxAllowedForPanCrust: number
) => ({
  title: 'Too much of a good thing...',
  body: `Stick with ${maxAllowedForPanCrust} or fewer toppings for the perfect Pan crust`,
  altCta: {
    text: "Don't Add",
    callback: altCtaCallback,
    reverseButtonsOrder: true
  },
  cta: {
    text: 'Add anyway',
    callback: ctaCallback,
    reverseButtonsOrder: true
  },
  hideCloseIcon: true
});

function isMaxAllowedToppingsWithinFreePriceRange(selectedCrust?: CrustOption): boolean {
  if (!selectedCrust) return false;
  const maxAllowed = selectedCrust?.maxAllowed ?? 0;
  const freePriceRange = selectedCrust?.slotPriceRanges?.slot_pizza_toppings.find((slot) => slot.price.amount === 0);
  let freePriceRangeEnd = 0;
  if (freePriceRange) {
    switch (freePriceRange.rangeEnd) {
      case undefined:
        freePriceRangeEnd = 0;
        break;
      case null: // null means there is no end to this price range
        freePriceRangeEnd = Infinity;
        break;
      default:
        freePriceRangeEnd = freePriceRange.rangeEnd;
        break;
    }
  }
  return freePriceRangeEnd > 0 && maxAllowed > 0 && freePriceRangeEnd >= maxAllowed;
}

export const renderToppingExtraPriceText = (
  extraPrice?: number | null,
  numberOfIncludedToppings?: number,
  selectedCrust?: CrustOption,
  isDealAndNewToppingTextEnabled?: boolean
): string => {
  const price = formattedPrice(extraPrice);
  if (isMaxAllowedToppingsWithinFreePriceRange(selectedCrust)) {
    return '';
  }
  if (!extraPrice) {
    return 'Additional toppings are an extra charge';
  }
  if ((numberOfIncludedToppings ?? 0) > 0) {
    if (isDealAndNewToppingTextEnabled) {
      return `${numberOfIncludedToppings} topping${(numberOfIncludedToppings ?? 0) > 1 ? 's' : ''} included. Extra toppings are ${price}`;
    }
    return `Additional toppings are ${price} each`;
  }
  return `${price} each`;
};

const excludeFromVariantWeights = [
  'LEFT_REGULAR',
  'LEFT_EXTRA',
  'RIGHT_REGULAR',
  'RIGHT_EXTRA',
  'WHOLE_REGULAR',
  'WHOLE_EXTRA'
];

const isExcludedFromVariant = (topping: PizzaIngredientOption, variantCode: string | undefined) => {
  if (!topping.excludedVariants || !variantCode) {
    return false;
  }

  return topping.weights?.every((weightKey) => {
    const variants = topping.excludedVariants?.[weightKey];
    return variants && variants.includes(variantCode);
  });
};

const isExcludedFromVariantLegacy = (topping: PizzaIngredientOption, variantCode: string | undefined) => {
  if (!topping.excludedVariants || !variantCode) {
    return false;
  }

  return excludeFromVariantWeights.every((weightKey) => {
    const variants = topping.excludedVariants?.[weightKey];
    return variants && variants.includes(variantCode);
  });
};

const ToppingsPicker = ({
  title,
  toppingOptions,
  selectedToppings,
  onToppingsUpdate,
  extraPrice,
  reachedToppingLimit,
  numDisplayedItems = DEFAULT_MINIMUM_DISPLAYED_ITEMS,
  hideToppingsExtraPrice = false,
  hideTitle = false
}: ToppingsPickerProps): JSX.Element => {
  const classes = useStyles();
  const sortedToppingList = getToppingsList(toppingOptions);
  const showAllToppings = shouldSeeAll(sortedToppingList, numDisplayedItems);
  const [expandSeeAll, setSeeAll] = useState<boolean>(showAllToppings);
  const [{ enabled: ccpHardcodingDisabled }] = useDecision('cb-tavern_national_launch');
  const [{ enabled: isDynamicFinishersSectionEnabled }] = useDecision('dtg-1723-dynamic-finishers-section');
  const [{ enabled: isSupportExcludedVariantEnabled }] = useDecision('dtg-1714-support-excluded-variant');

  const isReachedMaxToppings = useSelector(selectors.isReachedMaxToppings);

  const crustOption = useSelector(selectors.selectPizzaCrustOption);
  const crustOptionMaxToppingsAllowed = crustOption?.maxAllowed;

  const isPanCrustToppingModalShown = useSelector(modalSelectors.isPanCrustToppingModalShown);

  const preventAddTopping = reachedToppingLimit || isReachedMaxToppings;

  const showPanCrustModal = isReachedMaxToppings && !isPanCrustToppingModalShown;

  const selectedCrust = useSelector(selectors.selectPizzaCrust);
  const selectRecipeDefaultToppingsCount = useSelector(selectors.selectRecipeDefaultToppingsCount);
  const panCrust = isPanCrust(selectedCrust);
  const dispatch = useDispatch();
  const toppingElList = useRef<(HTMLDivElement | null)[]>([]);
  const { isLineup } = useLineup();

  useEffect(() => {
    if (expandSeeAll) {
      const firstHiddenTopping = toppingElList.current[numDisplayedItems];
      (firstHiddenTopping?.firstChild as HTMLDivElement | undefined)?.focus();
    }
  }, [expandSeeAll, numDisplayedItems]);

  const analytics = useAnalytics();
  const { pizza } = analytics.analyticsDataModel;

  const toggleSeeAll = () => {
    const toggledSeeAll = !expandSeeAll;
    setSeeAll(toggledSeeAll);

    if (toggledSeeAll) {
      analytics.push(() => onPizzaToppingSeeAllClick(pizza.name ?? '', title, 'See All', isLineup));
    }
  };

  const toppingsList = expandSeeAll ? sortedToppingList : sortedToppingList.slice(0, numDisplayedItems);
  const findSelectedToppingOptions = (toppingId: string | undefined) =>
    selectedToppings?.filter((ingredient) => ingredient.id !== toppingId) || [];

  const findToppingPortion = (toppingId: string | undefined) =>
    selectedToppings?.find((ingredient: PizzaIngredient) => ingredient.id === toppingId)?.portion;

  const addToppingWithPortionAndPlacement = (
    selectedIngredientToppings: PizzaIngredient[],
    toppingToAdd: PizzaIngredientOption,
    { hasExtra, placement }: OnToppingItemChangeProps
  ) => {
    const portion = hasExtra ? Portion.EXTRA : Portion.REGULAR;
    onToppingsUpdate([
      ...selectedIngredientToppings,
      toIngredientWithPortionAndPlacement(toppingToAdd, portion, placement)
    ]);
  };

  const sendDataAnalytics = (isExtraPortion: boolean, toppingName: string | null | undefined) => {
    const action = isExtraPortion ? ToppingsExtraAnalytics.EXTRA : ToppingsSelectionAnalytics.ADD;
    analytics.push(() => onPizzaToppingClick(pizza.name ?? '', toppingName, action, isLineup));
  };

  const onPreventToAdd = (toppingCount: number) => {
    dispatch(
      openModal({
        title: 'Topping overload!',
        body: `For an evenly cooked pizza, our chef allows up to ${toppingCount} ingredients`,
        cta: {
          text: 'GOT IT'
        }
      })
    );
  };

  const openPanCrustToppingsWarningModal = (
    selectedToppingOptions: PizzaIngredient[],
    toppingToAdd: PizzaIngredientOption,
    toppingChangedProp: OnToppingItemChangeProps,
    maxAllowedForPanCrust: number
  ) => {
    const ctaCallback = () => {
      addToppingWithPortionAndPlacement(selectedToppingOptions, toppingToAdd, toppingChangedProp);
      sendDataAnalytics(toppingChangedProp.hasExtra, toppingToAdd.name);
      dispatch(updatePanCrustToppingModalShown(true));
    };

    const altCtaCallback = () => {
      dispatch(updatePanCrustToppingModalShown(true));
    };

    dispatch(openModal(panCrustToppingMaxModalDetails(ctaCallback, altCtaCallback, maxAllowedForPanCrust)));
  };

  const veggies = useSelector(selectors.selectPizzaVeggieToppings);
  const meats = useSelector(selectors.selectPizzaMeatToppings);
  const showBNYToppingModal = (toppingId: string, isExtra: boolean) => {
    const isExistingToppings =
      meats?.find((meat) => meat.id === toppingId) || veggies?.find((veggie) => veggie.id === toppingId);

    if (isReachedMaxToppings) {
      if (isExistingToppings) {
        return isExtra;
      }
      return true;
    }
    return false;
  };
  const isBNY =
    selectedCrust?.id?.includes(constants.BNY_CRUST_ID) || selectedCrust?.id?.includes(constants.YUM_BNY_CRUST_ID);

  const handleToppingChange = (topping: PizzaIngredientOption, toppingChangedProp: OnToppingItemChangeProps) => {
    const portion = findToppingPortion(topping.id);
    const selectedToppingOptions = findSelectedToppingOptions(topping.id);
    const { hasExtra, isSelected, placement } = toppingChangedProp;
    const canAddPanCrustTopping = panCrust && showPanCrustModal;
    const showBNYCrustToppingModal = isBNY && showBNYToppingModal(topping.id, hasExtra);
    const isRegularPortion = !portion || (portion === Portion.REGULAR && hasExtra);
    const unselectExtra = portion === Portion.EXTRA && !hasExtra;
    const unselectTopping = !isSelected;
    // Handle finisher selection through toppings (legacy behavior when dynamic finishers section is not enabled)
    if (!isDynamicFinishersSectionEnabled && topping.slotCode === SlotKeys.pizzaSeasoning) {
      if (!isSelected) {
        onToppingsUpdate(selectedToppingOptions);
      } else {
        
        const nonFinishers = selectedToppingOptions.filter((t) => t.slotCode !== SlotKeys.pizzaSeasoning);
        const newToppings = [
          ...nonFinishers,
          toIngredientWithPortionAndPlacement(topping, Portion.REGULAR, placement)
        ]
        onToppingsUpdate(newToppings);
      }
      return;
    }

    const determineToppingLimit = (toppingLimitConstant: number) =>
      crustOptionMaxToppingsAllowed || toppingLimitConstant;

    switch (true) {
      case unselectTopping: {
        onToppingsUpdate(selectedToppingOptions);
        return;
      }
      case unselectExtra: {
        onToppingsUpdate([
          ...selectedToppingOptions,
          toIngredientWithPortionAndPlacement(topping, Portion.REGULAR, placement)
        ]);
        return;
      }
      case canAddPanCrustTopping: {
        openPanCrustToppingsWarningModal(
          selectedToppingOptions,
          topping,
          toppingChangedProp,
          determineToppingLimit(constants.MAX_TOPPINGS_ALLOWED_FOR_PAN_CRUST)
        );
        return;
      }
      case preventAddTopping && isRegularPortion: {
        onPreventToAdd(determineToppingLimit(constants.MAX_TOPPINGS_ALLOWED));
        return;
      }
      case showBNYCrustToppingModal: {
        onPreventToAdd(determineToppingLimit(constants.MAX_TOPPINGS_ALLOWED_FOR_BNY_CRUST));
        return;
      }
      default: {
        addToppingWithPortionAndPlacement(selectedToppingOptions, topping, toppingChangedProp);
      }
    }
  };

  // Hide Crispy Cupped Peppers if not Detroit-Style, Big New Yorker, Hot Honey or Tavern
  const currentCrust = useSelector(selectors.selectPizzaCrustOption);
  const currentPizzaRecipe = useSelector(selectors.selectPizzaOptions);
  const isDetroitPizza = currentCrust?.name === DETROIT_PIZZA;
  const isTavernPizza = currentCrust?.name === TAVERN_PIZZA;
  const isBigNewYorkerDealsDP = currentPizzaRecipe?.summary?.displayName === BIG_NEW_YORKER_DOUBLE_PEPPERONI;
  const isBigNewYorkerDP = currentPizzaRecipe?.summary?.name === BIG_NEW_YORKER_DOUBLE_PEPPERONI;
  const isHotHoney =
    currentPizzaRecipe?.summary?.displayName?.includes(HOT_HONEY_PARTIAL) ||
    currentPizzaRecipe?.summary?.name?.includes(HOT_HONEY_PARTIAL);
  const shouldShowCrispyCuppedPepperoni =
    ccpHardcodingDisabled || isDetroitPizza || isBigNewYorkerDealsDP || isBigNewYorkerDP || isHotHoney || isTavernPizza;
  // Putting this logic behind cb-tavern_national_launch to enable Crispy Cupped Peppers for every pizza when flag is on
  // Hide Crispy Cupped Peppers if not Detroit-Style, Big New Yorker, Hot Honey or Tavern ^^^

  const filteredToppingsList = useMemo(() => {
    const removedExcludedVariants = toppingsList.filter((topping) => isSupportExcludedVariantEnabled ? !isExcludedFromVariant(topping, currentCrust?.variantCode) : !isExcludedFromVariantLegacy(topping, currentCrust?.variantCode));
    
    // Filter out finishers when dynamic finishers section is enabled
    let toppingsWithoutFinishers = removedExcludedVariants;
    if (isDynamicFinishersSectionEnabled) {
      toppingsWithoutFinishers = removedExcludedVariants.filter((topping) => topping.internalName !== IngredientInternalName.FINISHERS);
    }
    
    const toppings =
      shouldShowCrispyCuppedPepperoni && title === 'Meats'
        ? toppingsWithoutFinishers
        : toppingsWithoutFinishers.filter((topping) => topping?.name !== CRISPY_CUPPED_PEPPERONI);
    return toppings;
  }, [
    currentCrust?.variantCode,
    isSupportExcludedVariantEnabled,
    shouldShowCrispyCuppedPepperoni,
    title,
    toppingsList,
    isDynamicFinishersSectionEnabled
  ]);

  return (
    <Grid item className={classes.root} data-testid={`${title.toLowerCase()}-toppings-picker`}>
      <div className={!hideTitle || !hideToppingsExtraPrice ? classes.header : ''}>
        {!hideTitle && <Typography variant="h3">{title}</Typography>}
        {!hideToppingsExtraPrice && (
          <Typography className={classes.priceDescription}>
            {renderToppingExtraPriceText(extraPrice, selectRecipeDefaultToppingsCount)}
          </Typography>
        )}
      </div>
      {filteredToppingsList.map((topping, index) => (
        <div
          key={`${index}-${topping.name}`}
          className={classes.picker}
          data-testid="toppings-list-item"
          ref={(element) => {
            toppingElList.current[index] = element;
          }}
        >
          <ToppingItemOptimized
            reachedToppingLimit={preventAddTopping}
            reachedPanCrustLimit={showPanCrustModal}
            reachedBNYCrustLimit={isBNY && isReachedMaxToppings}
            topping={topping}
            selectedIngredient={selectedToppings?.find((selectedTopping) => selectedTopping.id === topping.id)}
            extraPrice={extraPrice}
            onChange={handleToppingChange}
          />
        </div>
      ))}
      {sortedToppingList.length > numDisplayedItems && (
        <Grid container justifyContent="center">
          <SeeAllButton expanded={expandSeeAll} onClick={toggleSeeAll} accessibleGroupName={title} />
        </Grid>
      )}
    </Grid>
  );
};

export default ToppingsPicker;
